import bgImage from "./../../../assets/images/whisperbi_fondo_login.jpg";
import { Link, useNavigate } from "react-router-dom";
import InputForm from "../../../components/form/InputForm";
import { useEffect, useState } from "react";
import { signUp, confirmSignUp, resendSignUpCode, autoSignIn } from '@aws-amplify/auth';
import ErrorAlert from "../../../components/common/alerts/ErrorAlert";
import Loader from "../../../components/common/loaders/Loader";
import { CheckIcon } from "../../../assets/icons";
import { Tooltip } from "react-tooltip";
import {
  containsNumbers,
  containsCapitalLetters,
  containsLowercaseLetters,
  containsSpecialChars,
} from "../../../utils";

const Register = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1);
  const [progress, setProgress] = useState(0);

  // password validations
  const [hasNumbers, setHasNumbers] = useState(false);
  const [hasCapitalLetters, setHasCapitalLetters] = useState(false);
  const [hasLowercaseLetters, setHasLowercaseLetters] = useState(false);
  const [hasSpecialCharacters, setHasSpecialCharacters] = useState(false);
  const [hasEightChars, setHasEightChars] = useState(false);

  // step validations
  const [stepOneValidation, setStepOneValidation] = useState(false);
  const [stepTwoValidation, setStepTwoValidation] = useState(false);

  const generateAccountUuid = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    const timestampSegment = Date.now().toString(16);
    const randomSegment = Math.random().toString(16).slice(2, 10);
    return `${timestampSegment}-${randomSegment}`;
  };

  const registerUser = async () => {
    setLoading(true);
    try {
      const trimmedEmail = email.trim();
      const trimmedName = name.trim();
      const accountUuid = generateAccountUuid();

      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('account_id', accountUuid);
        }
      } catch (storageError) {
        console.warn('No pudimos almacenar el account_id localmente:', storageError);
      }

      // El rol "root" ya no se asigna desde el cliente (antes se marcaba
      // `custom:is_root: true` en TODO usuario que se auto-registraba, un
      // atributo editable desde el cliente -- efectivamente cualquiera
      // podía autoasignarse privilegios de plataforma). Ahora la
      // pertenencia al grupo de Cognito "root" solo puede asignarla un
      // administrador server-side (AdminAddUserToGroup).
      await signUp({
        username: trimmedEmail,
        password: password,
        attributes: {
          name: trimmedName,
          'custom:account_id': accountUuid
        },
        autoSignIn: { enabled: true },
      });
      setStep(2);
      setError("");
    } catch (err) {
      console.log("registerUser error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    try {
      await resendSignUpCode({ username: email.trim() });
      setError('');
    } catch (err) {
      setError(err?.message ?? 'No se pudo reenviar el código.');
    }
  };

  const verifyUserAccount = async () => {
    setLoading(true);
    try {
      if (!email?.trim()) {
        setError('Falta el correo para confirmar el registro.');
        return;
      }
      if (!code?.trim()) {
        setError('Ingresa el código de verificación.');
        return;
      }

      await confirmSignUp({
        username: email.trim(),
        confirmationCode: code.trim(),
      });

      // Con Amplify v6, pasar `autoSignIn: { enabled: true }` a signUp() no
      // autologuea por sí solo -- hay que llamar autoSignIn() explícitamente
      // tras confirmSignUp(). Sin esto, el usuario quedaba confirmado pero
      // sin sesión activa, y PrivateRoute lo rebotaba a /login pese al
      // mensaje de "serás redirigido al dashboard".
      const signInOutput = await autoSignIn();
      const signedIn =
        signInOutput?.nextStep?.signInStep === 'DONE' ||
        signInOutput?.nextStep?.signInStep === 'COMPLETE_AUTO_SIGN_IN';

      if (!signedIn) {
        setError('Tu cuenta se confirmó, pero no pudimos iniciar sesión automáticamente. Inicia sesión manualmente.');
        navigate('/login');
        return;
      }

      setError('');
      setStep(3);
    } catch (err) {
      console.log('verifyUserAccount err:', err);
      setError(err?.message ?? 'No se pudo confirmar la cuenta.');
    } finally {
      setLoading(false);
    }
  };

  const getConfirmPasswordClass = (password, confirmPassword) => {
    if (confirmPassword.length < 7) return "hidden";
    if (password === confirmPassword) return "hidden";
    return "animate__animated animate__fadeIn";
  };

  const validatePassword = (password, confirmPassword) => {
    const validation = {
      hasNumbers: containsNumbers(password),
      hasCapitalLetters: containsCapitalLetters(password),
      hasLowercaseLetters: containsLowercaseLetters(password),
      hasSpecialCharacters: containsSpecialChars(password),
      hasEightCharacters: password.length >= 8,
    };

    const isValid =
      validation.hasNumbers &&
      validation.hasCapitalLetters &&
      validation.hasLowercaseLetters &&
      validation.hasSpecialCharacters &&
      validation.hasEightCharacters &&
      password === confirmPassword;

    return { validation, isValid };
  };

  useEffect(() => {
    const { validation, isValid } = validatePassword(password, confirmPassword, code);

    setHasNumbers(validation.hasNumbers);
    setHasCapitalLetters(validation.hasCapitalLetters);
    setHasLowercaseLetters(validation.hasLowercaseLetters);
    setHasSpecialCharacters(validation.hasSpecialCharacters);
    setHasEightChars(validation.hasEightCharacters);

    setStepOneValidation(isValid && email.length >= 3 && name.length >= 3);
    setStepTwoValidation(code.length >= 3 && email.trim().length >= 3);
  }, [password, confirmPassword, code, name, email]);

  useEffect(() => {
    if (step === 3) {
      const intervalId = setInterval(() => setProgress((p) => p + 1), 40);
      if (progress === 100) navigate("/layautapp");
      return () => clearInterval(intervalId);
    }
  }, [step, progress, navigate]);

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-black text-white">
      {/* Columna izquierda (formulario) */}
      <section className="w-full lg:w-1/2 h-full mx-auto">
        <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
          <div className="w-full rounded-2xl border border-orange-500/60 bg-zinc-900/70 backdrop-blur shadow-xl md:mt-0 sm:max-w-md xl:p-0">
            <div className="p-6 sm:p-8 space-y-6">
              {step === 1 && (
                <div className="space-y-8">
                  <div className="flex items-center mb-6">
                    <Link
                      to="/login"
                      className="h-8 w-8 flex items-center justify-center rounded-full border border-orange-500/60 text-orange-500 hover:text-orange-400 hover:border-orange-400 transition"
                      aria-label="Volver a iniciar sesión"
                    >
                      <svg
                        fill="none"
                        className="w-4 h-4"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                        ></path>
                      </svg>
                    </Link>
                    <div className="w-full text-center">
                      <h1 className="text-2xl md:text-3xl font-bold">
                        Registro
                      </h1>
                      <span className="block mx-auto mt-2 h-1 w-16 bg-orange-500 rounded"></span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <InputForm
                      name="name"
                      placeholder="John Doe"
                      type="text"
                      title="Nombre completo"
                      value={name}
                      setValue={setName}
                    />
                    <InputForm
                      name="email"
                      placeholder="john@email.com"
                      type="email"
                      title="Correo electrónico"
                      value={email}
                      setValue={setEmail}
                    />
                    <InputForm
                      name="password"
                      placeholder="••••••••"
                      type="password"
                      title="Contraseña"
                      value={password}
                      setValue={setPassword}
                    />

                    {/* Reglas de contraseña */}
                    <div className={`pl-4 ${!password ? "hidden" : "animate__animated animate__fadeIn"}`}>
                      <ul className="text-xs text-gray-300">
                        <li className="flex items-center">
                          <span className="mr-1">
                            <CheckIcon isChecked={hasEightChars} />
                          </span>
                          Longitud mínima de 8 caracteres
                        </li>
                        <li className="flex items-center">
                          <span className="mr-1">
                            <CheckIcon isChecked={hasLowercaseLetters} />
                          </span>
                          Al menos una minúscula (a-z)
                        </li>
                        <li className="flex items-center">
                          <span className="mr-1">
                            <CheckIcon isChecked={hasCapitalLetters} />
                          </span>
                          Al menos una mayúscula (A-Z)
                        </li>
                        <li className="flex items-center">
                          <span className="mr-1">
                            <CheckIcon isChecked={hasNumbers} />
                          </span>
                          Al menos un número (0-9)
                        </li>
                        <li className="flex items-center">
                          <span className="mr-1">
                            <CheckIcon isChecked={hasSpecialCharacters} />
                          </span>
                          <span
                            className="underline cursor-pointer"
                            data-tooltip-id="pwd-tooltip"
                            data-tooltip-place="top"
                            data-tooltip-content=" ^ $ * . [ ] { } ( ) ? &quot; - ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ ` + = "
                          >
                            Al menos un caracter especial
                          </span>
                          <Tooltip id="pwd-tooltip" />
                        </li>
                      </ul>
                    </div>

                    <InputForm
                      name="confirmPassword"
                      placeholder="••••••••"
                      type="password"
                      title="Confirmar contraseña"
                      value={confirmPassword}
                      setValue={setConfirmPassword}
                    />

                    <div className={`pl-4 ${getConfirmPasswordClass(password, confirmPassword)}`}>
                      <ul className="text-xs text-orange-400">
                        <li className="flex items-center">
                          <span className="mr-1">
                            <CheckIcon isChecked={false} />
                          </span>
                          Las contraseñas no coinciden
                        </li>
                      </ul>
                    </div>

                    {error && <ErrorAlert error={error} />}

                    <div>
                      <button
                        disabled={!stepOneValidation}
                        onClick={registerUser}
                        className="w-full py-3 font-semibold rounded-full bg-black text-white border-2 border-orange-500 enabled:hover:bg-black/90 enabled:hover:border-orange-400 enabled:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
                      >
                        Crear cuenta
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-8">
                  <div className="text-center">
                    <h1 className="text-2xl md:text-3xl font-bold">Registro</h1>
                    <span className="block mx-auto mt-2 h-1 w-16 bg-orange-500 rounded"></span>
                  </div>

                  <div className="space-y-4">
                    <p className="text-sm text-gray-300">
                      Te enviamos un código para verificar tu correo electrónico. Por favor, ingrésalo en el siguiente campo.
                    </p>

                    <InputForm
                      name="code"
                      placeholder="Ingresa el código"
                      type="text"
                      title=""
                      value={code}
                      setValue={setCode}
                    />

                    {error && <ErrorAlert error={error} />}

                    <div>
                      <button
                        disabled={!stepTwoValidation}
                        onClick={verifyUserAccount}
                        className="w-full py-3 font-semibold rounded-full bg-black text-white border-2 border-orange-500 enabled:hover:bg-black/90 enabled:hover:border-orange-400 enabled:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
                      >
                        Enviar
                      </button>
                    </div>

                    <div className="text-right">
                      <button
                        type="button"
                        onClick={resendCode}
                        className="text-xs text-orange-500 hover:text-orange-400 transition"
                        disabled={!email.trim()}
                      >
                        Reenviar código
                      </button>
                    </div>

                    <hr className="border-zinc-700" />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="text-center py-5 animate__animated animate__fadeIn">
                  <span className="text-2xl font-extrabold">¡Registro Exitoso!</span>
                  <div className="flex justify-center my-5">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-28 h-28 fill-orange-500 animate__animated animate__flip"
                    >
                      <path
                        fillRule="evenodd"
                        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="text-sm mb-7 text-gray-300">
                    Tu cuenta se ha creado exitósamente
                  </p>

                  {/* Progress bar */}
                  <div className="h-3 w-full mb-2 bg-zinc-800 rounded-md overflow-hidden">
                    <div
                      className="h-full bg-orange-500"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-400">
                    En breve serás redirigido al dashboard
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Columna derecha (imagen) */}
      <section
        className="relative bg-cover bg-center bg-no-repeat w-1/2 h-full hidden lg:block"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        {/* Overlay oscuro para contraste */}
        <div className="absolute inset-0 bg-black/50" />
      </section>
    </div>
  );
};

export default Register;
